class SouqAtelierTheme {
  constructor() {
    this.body = document.body;
    this.cartDrawer = document.querySelector('[data-cart-drawer]');
    this.overlay = document.querySelector('[data-overlay]');
    this.searchPanel = document.querySelector('[data-search-panel]');
    this.header = document.querySelector('[data-header]');
    this.init();
  }

  init() {
    this.bindOverlayControls();
    this.bindScrollEffects();
    this.bindRevealEffects();
    this.bindRailControls();
    this.bindQuantityControls();
    this.bindVariantPickers();
    this.bindAjaxCart();
    this.bindSortForms();
    this.bindPointerHighlights();
  }

  bindOverlayControls() {
    document.querySelectorAll('[data-search-open]').forEach((button) => {
      button.addEventListener('click', () => this.openSearch());
    });

    document.querySelectorAll('[data-search-close], [data-cart-close], [data-overlay]').forEach((button) => {
      button.addEventListener('click', () => this.closeOverlays());
    });

    document.querySelectorAll('[data-cart-open]').forEach((button) => {
      button.addEventListener('click', (event) => {
        if (!this.cartDrawer) return;
        event.preventDefault();
        this.openCart();
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeOverlays();
    });
  }

  openSearch() {
    if (!this.searchPanel) return;
    this.closeOverlays();
    this.searchPanel.classList.add('is-open');
    this.overlay?.classList.add('is-visible');
    this.body.classList.add('search-open');
    window.setTimeout(() => this.searchPanel.querySelector('input')?.focus(), 180);
  }

  openCart() {
    if (!this.cartDrawer) return;
    this.closeOverlays();
    this.cartDrawer.classList.add('is-open');
    this.overlay?.classList.add('is-visible');
    this.body.classList.add('drawer-open');
  }

  closeOverlays() {
    this.searchPanel?.classList.remove('is-open');
    this.cartDrawer?.classList.remove('is-open');
    this.overlay?.classList.remove('is-visible');
    this.body.classList.remove('search-open', 'drawer-open');
  }

  bindScrollEffects() {
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? window.scrollY / max : 0;
      document.documentElement.style.setProperty('--scroll', progress.toFixed(4));
      document.documentElement.style.setProperty('--parallax', Math.min(window.scrollY / 720, 1).toFixed(3));
      this.header?.classList.toggle('is-elevated', window.scrollY > 16);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  bindRevealEffects() {
    const reveals = [...document.querySelectorAll('.reveal')];
    if (!('IntersectionObserver' in window)) {
      reveals.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12 }
    );

    reveals.forEach((item) => observer.observe(item));
  }

  bindRailControls() {
    document.querySelectorAll('[data-rail-prev]').forEach((button) => {
      button.addEventListener('click', () => {
        button.closest('.product-rail')?.querySelector('[data-product-rail]')?.scrollBy({ left: -360, behavior: 'smooth' });
      });
    });

    document.querySelectorAll('[data-rail-next]').forEach((button) => {
      button.addEventListener('click', () => {
        button.closest('.product-rail')?.querySelector('[data-product-rail]')?.scrollBy({ left: 360, behavior: 'smooth' });
      });
    });
  }

  bindQuantityControls() {
    document.querySelectorAll('.quantity-control').forEach((control) => {
      const input = control.querySelector('[data-quantity-input]');
      if (!input) return;

      control.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
        input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      control.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
        input.value = Number(input.value || 1) + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  bindVariantPickers() {
    document.querySelectorAll('[data-product-section]').forEach((section) => {
      const variantsNode = section.querySelector('[data-product-variants]');
      const idInput = section.querySelector('[data-variant-id]');
      if (!variantsNode || !idInput) return;

      let variants = [];
      try {
        variants = JSON.parse(variantsNode.textContent);
      } catch (error) {
        return;
      }

      const updateVariant = () => {
        const selectedOptions = [...section.querySelectorAll('.variant-pickers fieldset')].map((fieldset) => {
          return fieldset.querySelector('input:checked')?.value;
        });
        const variant = variants.find((item) => selectedOptions.every((value, index) => item.options[index] === value));
        if (!variant) return;

        idInput.value = variant.id;
        const submit = section.querySelector('[name="add"]');
        if (submit) {
          submit.disabled = !variant.available;
          submit.textContent = variant.available ? window.themeStrings?.addToCart || 'Add to cart' : window.themeStrings?.soldOut || 'Sold out';
        }

        const price = section.querySelector('[data-price]');
        if (price) price.textContent = this.formatMoney(variant.price);
      };

      section.querySelectorAll('.variant-pickers input').forEach((input) => input.addEventListener('change', updateVariant));
    });
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  }

  bindAjaxCart() {
    document.querySelectorAll('.product-form').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        if (!this.cartDrawer || !window.fetch) return;
        event.preventDefault();

        const submit = form.querySelector('[name="add"]');
        submit?.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
          });
          if (!response.ok) throw new Error('Cart add failed');
          await this.refreshCartDrawer();
          this.openCart();
        } catch (error) {
          form.submit();
        } finally {
          submit?.removeAttribute('aria-busy');
        }
      });
    });
  }

  async refreshCartDrawer() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();

    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = cart.item_count;
    });

    document.querySelectorAll('[data-cart-subtotal]').forEach((node) => {
      node.textContent = this.formatMoney(cart.total_price);
    });

    const itemsNode = document.querySelector('[data-cart-drawer-items]');
    if (!itemsNode) return;

    if (!cart.items.length) {
      itemsNode.innerHTML = '<p>Your cart is waiting for its first piece.</p>';
      return;
    }

    itemsNode.innerHTML = cart.items.map((item) => `
      <article class="cart-item">
        ${item.image ? `<img src="${item.image}" alt="${this.escapeHtml(item.product_title)}" loading="lazy">` : '<div class="cart-mini-visual" aria-hidden="true"></div>'}
        <div>
          <h3>${this.escapeHtml(item.product_title)}</h3>
          <span>${item.quantity} x ${this.formatMoney(item.final_price)}</span>
        </div>
        <strong>${this.formatMoney(item.final_line_price)}</strong>
      </article>
    `).join('');
  }

  bindSortForms() {
    document.querySelectorAll('[data-sort-form] select').forEach((select) => {
      select.addEventListener('change', () => select.form.submit());
    });
  }

  bindPointerHighlights() {
    document.addEventListener('mousemove', (event) => {
      const target = event.target.closest('.category-tile, .product-card__media, .product-visual');
      if (!target) return;
      const rect = target.getBoundingClientRect();
      target.style.setProperty('--x', `${event.clientX - rect.left}px`);
      target.style.setProperty('--y', `${event.clientY - rect.top}px`);
    });
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');
window.themeStrings = {
  addToCart: 'Add to cart',
  soldOut: 'Sold out',
};
new SouqAtelierTheme();
